import React, { useState } from 'react';
import { AlertTriangle, Info, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { LegalNoticeModal } from './LegalNoticeModal';

interface FinancialDisclaimerBannerProps {
  className?: string;
  variant?: 'inline' | 'floating' | 'card';
  uiLanguage?: 'ko' | 'en';
}

export function FinancialDisclaimerBanner({
  className = '',
  variant = 'inline',
  uiLanguage = 'ko'
}: FinancialDisclaimerBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isKo = uiLanguage === 'ko';

  return (
    <>
      <div 
        className={`w-full rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-3 transition-all ${className}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5">
            <div className="p-1 rounded-md bg-amber-500/10 text-amber-400 mt-0.5 shrink-0">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-bold text-amber-400 uppercase tracking-wider">
                  {isKo ? '금융투자 데이터 면책 공지' : 'Financial Disclaimer'}
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 font-mono">
                  SaaS Data Tool
                </span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono border border-emerald-500/30">
                  {isKo ? '모의투자 시뮬레이션 환경' : 'Simulation Mode'}
                </span>
              </div>
              <p className="text-xs text-zinc-300 font-medium leading-relaxed">
                {isKo 
                  ? '본 서비스가 제공하는 데이터 및 지표는 투자 참고용 정보이며, 특정 종목에 대한 투자 권유나 자문이 아니므로 투자 손익에 대한 법적 책임은 사용자 본인에게 있습니다.'
                  : 'All data and metrics provided by EZOptix are for reference only, do not constitute investment advice or a solicitation to trade securities, and legal responsibility for investment outcomes rests solely with the user.'}
              </p>

              {isExpanded && (
                <div className="pt-2 text-[11px] text-zinc-400 leading-relaxed border-t border-amber-500/10 space-y-1.5 animate-in fade-in duration-150">
                  <p>
                    {isKo
                      ? '• EZOptix(ezoptix.ai)는 「자본시장과 금융투자업에 관한 법률」상 투자자문업 및 유사투자자문업에 해당하지 않으며, 순수한 정량 데이터 시각화 및 알고리즘 연산 소프트웨어입니다.'
                      : '• EZOptix is a pure quantitative data visualization & algorithmic software (SaaS) and does not provide regulated investment advisory services.'}
                  </p>
                  <p>
                    {isKo
                      ? '• 과거의 백테스팅 데이터와 매크로 지표는 미래 수익을 보장하지 않으며, 모든 금융투자 상품 거래의 원금 손실 위험은 이용자 본인에게 전적으로 귀속됩니다.'
                      : '• Past telemetry and backtesting do not guarantee future returns. All financial investment risks remain solely with the user.'}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 text-zinc-400 hover:text-white rounded transition-colors"
              title={isExpanded ? '접기' : '더보기'}
              aria-label={isExpanded ? '접기' : '더보기'}
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-2 py-1 text-[10px] text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 rounded font-medium transition-colors flex items-center gap-1"
            >
              <span>{isKo ? '전문 보기' : 'Full Notice'}</span>
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      <LegalNoticeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialTab="DISCLAIMER"
        uiLanguage={uiLanguage}
      />
    </>
  );
}
